import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme, THEMES } from '@/contexts/ThemeContext';
import { Check } from 'lucide-react';

export const ThemeSwitcher = () => {
  const { theme, setTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const current = THEMES.find((t) => t.id === theme) || THEMES[0];

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative" data-testid="theme-switcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="theme-switcher-trigger"
        aria-label={`Theme: ${current.id === 'bedtime' ? 'Bedtime' : 'Magical Glow'}`}
        title={current.id === 'bedtime' ? 'Bedtime' : 'Magical Glow'}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border theme-pill-btn transition-all duration-200 hover:scale-105 active:scale-95"
      >
        <span className="flex gap-0.5" aria-hidden>
          {current.swatches.map((c, i) => (
            <span
              key={i}
              className="w-3 h-3 rounded-full block"
              style={{ background: c }}
            />
          ))}
        </span>
        <motion.span
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="text-[10px] leading-none opacity-60"
          aria-hidden
        >
          ▾
        </motion.span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            className="absolute right-0 top-full mt-2 z-[200] rounded-2xl shadow-2xl overflow-hidden theme-dropdown"
            style={{ width: 88 }}
            data-testid="theme-dropdown"
          >
            <div className="p-2 space-y-1">
              {THEMES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  data-testid={`theme-option-${t.id}`}
                  aria-label={t.id === 'bedtime' ? 'Bedtime' : 'Magical Glow'}
                  title={t.id === 'bedtime' ? 'Bedtime' : 'Magical Glow'}
                  onClick={() => {
                    setTheme(t.id);
                    setOpen(false);
                  }}
                  className={`w-full flex items-center justify-center gap-2 px-2 py-2.5 rounded-xl transition-all duration-150 theme-dropdown-item ${
                    theme === t.id ? 'theme-dropdown-item-active' : ''
                  }`}
                >
                  <span className="flex gap-0.5 flex-shrink-0" aria-hidden>
                    {t.swatches.map((c, i) => (
                      <span
                        key={i}
                        className="w-4 h-4 rounded-full shadow-sm"
                        style={{ background: c }}
                      />
                    ))}
                  </span>
                  {theme === t.id ? (
                    <Check className="w-4 h-4 flex-shrink-0 theme-check-icon" aria-hidden />
                  ) : null}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
